/**
 * Admin Persona RPC definitions
 *
 * Admin endpoints for persona management - requires admin role.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Persona, PersonaStatus, QualityProfileSchema, ShadowProfileEntrySchema } from "@fm/db/frontend";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized } from "../errors.ts";

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Input schema for creating a draft persona - just title
 * @since 1.0.0
 */
export const PersonaCreateDraftInput = Schema.Struct({
  title: Schema.NullOr(Schema.String),
});

/**
 * Response schema for creating a draft persona
 * @since 1.0.0
 */
export const PersonaCreateDraftResponse = Schema.Struct({
  id: Schema.String,
  conversationId: Schema.NullOr(Schema.String),
});

/**
 * Input schema for updating a persona - optional fields plus required id.
 *
 * Defined manually because Schema.partial does not support Transformation
 * schemas (Persona.json uses optionalWith defaults on status/currentStep).
 *
 * @since 1.0.0
 */
export const PersonaUpdateInput = Schema.Struct({
  id: Schema.String,
  name: Schema.optional(Schema.NullOr(Schema.String)),
  age: Schema.optional(Schema.NullOr(Schema.Number)),
  occupation: Schema.optional(Schema.NullOr(Schema.String)),
  familyOfOrigin: Schema.optional(Schema.NullOr(Schema.String)),
  formativeExperiences: Schema.optional(Schema.NullOr(Schema.String)),
  psychologicalCore: Schema.optional(Schema.NullOr(Schema.String)),
  expressionInConversation: Schema.optional(Schema.NullOr(Schema.String)),
  shadowTriggers: Schema.optional(Schema.NullOr(Schema.String)),
  qualityProfile: Schema.optional(Schema.NullOr(QualityProfileSchema)),
  communicationMarkers: Schema.optional(Schema.Array(Schema.String)),
  characteristicPhrases: Schema.optional(Schema.Array(Schema.String)),
  shadowProfile: Schema.optional(Schema.Array(ShadowProfileEntrySchema)),
  sourceInspiration: Schema.optional(Schema.NullOr(Schema.String)),
  status: Schema.optional(PersonaStatus),
  currentStep: Schema.optional(Schema.Number),
});

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Persona RPC group
 *
 * All endpoints require admin role via AdminMiddleware.
 *
 * @since 1.0.0
 */
export class AdminPersonaRpcs extends RpcGroup.make(
  Rpc.make("admin.persona.createDraft", {
    payload: PersonaCreateDraftInput,
    success: PersonaCreateDraftResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.persona.findMany", {
    success: Schema.Array(Persona.json),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.persona.findById", {
    payload: Schema.Struct({ id: Schema.String }),
    success: Persona.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.persona.update", {
    payload: PersonaUpdateInput,
    success: Persona.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.persona.delete", {
    payload: Schema.Struct({ id: Schema.String }),
    success: Schema.Struct({ success: Schema.Boolean }),
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
) {}
